import React, {createContext, useContext, useReducer} from "react";
import _ from "lodash";

const initialState = {
    assets: {}, // hash by id
    tags: [],
    currentAsset: null,
}

const AssetsStateContext = createContext(initialState);
const AssetsDispatchContext = createContext(initialState);

// Merge new tags with existing tags, preserving fields from existing tags
// that may be missing in newer (incomplete) tag objects.
const mergeTags = (newTags, existingTags) => {
    const safeNew = _.compact(newTags);
    const safeExisting = _.compact(existingTags);
    const existingByTagId = _.keyBy(safeExisting, 'tag_id');
    const merged = safeNew.map(newTag => {
        const existing = existingByTagId[newTag.tag_id];
        // Spread existing first so new fields overwrite, but missing fields are preserved
        return existing ? {...existing, ...newTag} : newTag;
    });
    // Add any existing tags not in the new set
    const newTagIds = new Set(safeNew.map(t => t.tag_id));
    const remaining = safeExisting.filter(t => !newTagIds.has(t.tag_id));
    return [...merged, ...remaining];
};

// List responses contain a type summary; detail responses contain its schema.
// Keep hydrated fields only for the exact same type revision. A full response
// (including an empty schema), a changed type, or null remains authoritative.
const mergeAsset = (existing, incoming) => {
    const merged = {...existing, ...incoming};
    const previousType = existing?.entity_type;
    const nextType = incoming.entity_type;
    if (previousType && nextType && previousType.id === nextType.id &&
        previousType.updated_at && previousType.updated_at === nextType.updated_at &&
        !Object.hasOwn(nextType, 'fields')) {
        merged.entity_type = {...previousType, ...nextType};
    } else if (!Object.hasOwn(incoming, 'entity_type') && Object.hasOwn(incoming, 'entity_type_id') &&
        incoming.entity_type_id !== existing?.entity_type_id) {
        delete merged.entity_type;
    }
    return merged;
};

const reducer = (state, action) => {
    if (process.env.NODE_ENV === 'development') {
        console.log('AssetsContext reducer:', action.type, action);
    }

    let i, tags, newTags, newAsset;

    switch (action.type) {
        case 'assetsLoaded':
            newTags = _.compact(_.flatten(action.assets.map(asset => asset.tags || [])));
            tags = _.uniqBy(_.compact([...state.tags, ...newTags]), 'tag_id')

            const loadedAssets = {...state.assets};
            action.assets.forEach(asset => {
                loadedAssets[asset.id] = mergeAsset(loadedAssets[asset.id], asset)
            })

            return {...state, assets: loadedAssets, tags};

        case 'assetsAdded':
            newTags = _.compact(_.flatten(action.assets.map(asset => asset.tags || [])));
            tags = _.uniqBy(_.compact([...state.tags, ...newTags]), 'tag_id')

            const addedAssets = {...state.assets};
            action.assets.forEach(asset => {
                // useShowAsset supplies an authoritative response, including
                // omissions when this viewer can no longer see private detail.
                addedAssets[asset.id] = asset
            })

            return {...state, assets: addedAssets, tags};

        case 'updateAsset':
            newTags = action.asset.tags || []
            tags = mergeTags(newTags, state.tags)

            const updatedAssets = {...state.assets};
            updatedAssets[action.asset.id] = mergeAsset(updatedAssets[action.asset.id], action.asset)

            return {...state, assets: updatedAssets, tags};

        case 'addTags':
            tags = _.uniqBy([...state.tags, ...action.tags], 'tag_id');
            return {...state, tags}

        case 'updateTag':
            const tag = _.find(state.tags, {tag_id: action.tag.tag_id})
            i = _.indexOf(state.tags, tag)
            const newTagsArray = [...state.tags];
            newTagsArray.splice(i, 1, action.tag);

            return {...state, tags: newTagsArray};

        default:
            return state;
    }
}

const AssetsProvider = ({children})=> {
    const [state, dispatch] = useReducer(reducer, initialState);
    return (
        <AssetsStateContext.Provider value={state}>
            <AssetsDispatchContext.Provider value={dispatch}>
                {children}
            </AssetsDispatchContext.Provider>
        </AssetsStateContext.Provider>
    )
}

function useAssetsState() {
    const context = useContext(AssetsStateContext)
    if (context === undefined) {
        throw new Error('useAssetState must be used within a AssetProvider')
    }
    return context
}

function useAssetsDispatch() {
    const context = useContext(AssetsDispatchContext)
    if (context === undefined) {
        throw new Error('useAssetDispatch must be used within a AssetProvider')
    }
    return context
}

export {
    AssetsProvider,
    useAssetsState,
    useAssetsDispatch,
    reducer,
    mergeTags,
    initialState
};
